import { FormState } from "core/FormState";
import { LanguageCodes, Misc } from "core/Misc";
import { Slider } from "core/Controls/Slider";
import { type Tab, Tabset } from "core/Controls/Tabset";
import { html } from "core/Controls/BookSearch.html";
import { type BookSearchField, type BookSearchLength, type BookSearchQuery } from "core/Services/SearchBooksService";
import { type CollectionView, type Event, type EventEmitter, EventListener, View, type ViewData } from "silver";
import { type BooksLang, type UserLang } from "Lang";
import { UserLangGradeShort } from "shared/LanguageExtensions";

export interface ButtonInputViewData<ID = string> {
	id: ID;
	label: string;
}

export interface LanguageButtonInputViewData<ID = string> extends ButtonInputViewData<ID> {
	lang: string;
}

export interface SelectableButtonInputViewData<ID = string> extends ButtonInputViewData<ID> {
	selected?: true;
}

export function isCheckbox(input: HTMLInputElement): boolean {
	if (input.tagName != "INPUT") {
		return false;
	}
	const type = String(input.getAttribute("type")).toLowerCase();
	return (type == "checkbox" || type == "radio");
}

export interface FiltersUpdatedEvent extends Event {
	filters: Array<keyof BookSearchQuery>;
}

export interface BookSearchInterface {
	readonly onChange: EventEmitter<Event>;
	readonly onFiltersUpdated: EventEmitter<FiltersUpdatedEvent>;
	readonly onFormSubmit: EventEmitter<Event>;
	/** @deprecated */
	state(): FormState;
	readonly element: HTMLElement;
	import(query: BookSearchQuery): void;
	export(): BookSearchQuery;
	resetField(fieldName: string): void;
	selectSearchFields(searchFields: BookSearchField[]): void;
}

export function optionArray(inArray: number[], label: (value: number) => string = String): Array<{ id: string, label: string }> {
	return inArray.map(i => {
		return { id: String(i), label: label(i) };
	});
}

type TabData = {
	label: string;
	iconClass: string;
	id: string;
};

interface BookSearchViewData extends ViewData {
	lang: BooksLang;
	searchFields: Array<ButtonInputViewData<BookSearchField>>;
	languageToggles: LanguageButtonInputViewData[];
	fictionToggles: ButtonInputViewData[];
	audioBtns: SelectableButtonInputViewData[];
	lengthBtns: Array<SelectableButtonInputViewData<BookSearchLength>>;
	tabs: TabData[];
	dark: boolean;
}

interface SearchFieldView extends View<ButtonInputViewData<BookSearchField>> {
	checkbox: HTMLInputElement;
}

interface BookSearchView extends View<BookSearchViewData> {
	searchBtns: HTMLUListElement;
	filterDisplay: HTMLDivElement;
	gradeLevelLabel: HTMLElement;
	gradeLevelFilter: HTMLElement;
	atosLevelLabel: HTMLElement;
	atosFilter: HTMLElement;
	guidedReadingFilter: HTMLElement;
	guidedReadingLabel: HTMLElement;
	lexileLabel: HTMLElement;
	lexileFilter: HTMLElement;
	fictionFilter: HTMLElement;
	languageFilter: HTMLElement;
	storyLengthFilter: HTMLElement;
	searchFields: CollectionView<ButtonInputViewData<BookSearchField>, SearchFieldView>;
	languageToggles: CollectionView<LanguageButtonInputViewData>;
	fictionToggles: CollectionView<ButtonInputViewData>;
	audioBtns: CollectionView<SelectableButtonInputViewData<BookSearchLength>>;
	lengthBtns: CollectionView<SelectableButtonInputViewData>;
	buttonContainer: HTMLDivElement;
	tabPanelContainer: HTMLElement;
	tabThumbContainer: HTMLElement;
	tabset: HTMLElement;
	tabs: CollectionView<TabData>;
}

type RangeSlider = Slider<[number, number], [string, string]>;
type BookSearchTabId = "fiction" | "lexile" | "atos" | "guidedReading" | "gradeLevel" | "language" | "storyLength";

export class BookSearch implements BookSearchInterface {

	public readonly onChange = EventListener.create<Event>("change");
	public readonly onFormSubmit = EventListener.create<Event>("submit");
	public readonly onFiltersUpdated = EventListener.create<FiltersUpdatedEvent>("filtersUpdated");

	private readonly view: BookSearchView;
	private readonly formState: FormState;
	private readonly guidedReadingSlider: RangeSlider;
	private readonly lexileSlider: RangeSlider;
	private readonly gradeLevelSlider: RangeSlider;
	private readonly atosSlider: RangeSlider;
	private readonly tabset: Tabset<BookSearchTabId>;

	public state(): FormState {
		return this.formState;
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	constructor(
		private lang: BooksLang,
		private userLang: UserLang,
		availableLanguages: number[]
	) {
		this.view = new View<BookSearchViewData>(html, {
			handleRadioAdd: (e) => {
				const input = e.element.querySelector(`input[type="radio"]`);
				if (!input || !e.data.selected) {
					return;
				}
				input.defaultChecked = e.data.selected;
			},
			handleResetButtonClick: () => {
				this.formState.restore();
				this.guidedReadingSlider.refresh();
				this.lexileSlider.refresh();
				this.gradeLevelSlider.refresh();
				this.atosSlider.refresh();

				return false;
			}
		}, {
			searchFields: [
				{ id: "title", label: this.lang.FILTER_TITLE() },
				{ id: "series", label: this.lang.FILTER_SERIES() },
				{ id: "author", label: this.lang.FILTER_AUTHOR() },
				{ id: "description", label: this.lang.FILTER_DESCRIPTION() },
				{ id: "publisher", label: this.lang.FILTER_PUBLISHER() }
			],
			languageToggles: [
				...availableLanguages.map(langId => ({
					id:    String(langId),
					label: Misc.languageTitle(langId),
					lang:  LanguageCodes[langId] || ""
				})),
				{ id: "bi", label: this.lang.FILTER_BILINGUAL(), lang: document.documentElement.lang }
			],
			audioBtns: [
				{ id: "", label: this.lang.FILTER_ANY(), selected: true },
				{ id: "1", label: this.lang.FILTER_YES() },
				{ id: "0", label: this.lang.FILTER_NO() }
			],
			fictionToggles: [
				{ id: "0", label: this.lang.FILTER_NON_FICTION() },
				{ id: "1", label: this.lang.FILTER_FICTION() }
			],
			lengthBtns: [
				{ id: "any", label: this.lang.FILTER_ANY(), selected: true },
				{ id: "short", label: this.lang.FILTER_SHORT() },
				{ id: "medium", label: this.lang.FILTER_MEDIUM() },
				{ id: "long", label: this.lang.FILTER_LONG() },
				{ id: "epic", label: this.lang.FILTER_EPIC() }
			],
			lang,
			tabs: [],
			dark: true
		}) as BookSearchView;

		this.tabset = new Tabset(this.view.tabset, this.view.tabThumbContainer, this.view.tabPanelContainer);
		this.view.element.addEventListener("submit", (e) => {
			e.preventDefault();
			this.onFormSubmit.trigger({ });
		});

		const makeTab = (tabId: BookSearchTabId, iconClass: string, label: string, tabpanel: HTMLElement): Tab< BookSearchTabId> => {
			const tab = this.view.tabs.add(tabId, {
				id: tabId,
				iconClass,
				label
			});
			return { tabId, tab: tab.element, tabpanel };
		};

		this.gradeLevelSlider = this.initSlider(this.lang.LABEL_GRADE_LEVEL(), 14, "gradeLevel", 1, 14, 1, (gradeId: number) => UserLangGradeShort(this.userLang, gradeId), this.view.gradeLevelLabel, this.view.gradeLevelFilter);
		this.atosSlider = this.initSlider(this.lang.LABEL_ATOS(), 11, "atos", 0, 10, 1, Misc.atosLabel, this.view.atosLevelLabel, this.view.atosFilter);
		this.lexileSlider = this.initSlider(this.lang.LABEL_LEXILE(), 16, "lexile", 0, 1500, 50, Misc.formatLexile, this.view.lexileLabel, this.view.lexileFilter);
		this.guidedReadingSlider = this.initSlider(this.lang.LABEL_GUIDED_READING(), 26, "guidedReading", 0, 25, 1, Misc.guidedReadingLevel, this.view.guidedReadingLabel, this.view.guidedReadingFilter);

		this.tabset.onTabActivate(({ tab }) => {
			tab.tab.classList.add("-selected", "-checked");
			tab.tabpanel.hidden = false;
		});
		this.tabset.onTabDeactivate(({ tab }) => {
			tab.tab.classList.remove("-selected", "-checked");
			tab.tabpanel.hidden = true;
		});

		this.tabset.add(makeTab("fiction", "fIcon -fiction", lang.LABEL_FICTION(), this.view.fictionFilter));
		this.tabset.add(makeTab("lexile", "fIcon -lexile", lang.LABEL_LEXILE(), this.view.lexileFilter));
		this.tabset.add(makeTab("atos", "fIcon -atos", lang.LABEL_ATOS(), this.view.atosFilter));
		this.tabset.add(makeTab("guidedReading", "fIcon -guidedReading", lang.LABEL_GUIDED_READING(), this.view.guidedReadingFilter));
		this.tabset.add(makeTab("gradeLevel", "fIcon -gradeLevel", lang.LABEL_GRADE_LEVEL(), this.view.gradeLevelFilter));
		this.tabset.add(makeTab("language", "fIcon -language", lang.LABEL_LANGUAGE(), this.view.languageFilter));
		this.tabset.add(makeTab("storyLength", "fIcon -storyLength", lang.LABEL_BOOK_TYPE(), this.view.storyLengthFilter));

		this.formState = new FormState(this.view.element as HTMLFormElement);
		this.init();

		(window as any).bookSearch = this;

		this.setActiveFilter("fiction");
	}

	public setDarkMode(dark: boolean): void {
		this.view.dataset.dark = dark;
		this.guidedReadingSlider.setDarkMode(dark);
		this.lexileSlider.setDarkMode(dark);
		this.gradeLevelSlider.setDarkMode(dark);
		this.atosSlider.setDarkMode(dark);
	}

	public import(data: BookSearchQuery) {
		let inputs, i, input;
		if (typeof data.fiction !== "undefined") {
			input = this.view.element.querySelector("input[name=\"fiction[" + (+data.fiction) + "]\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		if (typeof data.gradeLevel !== "undefined") {
			this.gradeLevelSlider.value = data.gradeLevel;
		}
		if (typeof data.atos !== "undefined") {
			this.atosSlider.value = data.atos;
		}
		if (typeof data.lexile !== "undefined") {
			this.lexileSlider.value = data.lexile;
		}
		if (typeof data.guidedReading !== "undefined") {
			this.guidedReadingSlider.value = [
				parseInt(data.guidedReading[ 0 ], 36) - 10,
				parseInt(data.guidedReading[ 1 ], 36) - 10
			];
		}
		if (typeof data.search !== "undefined") {
			inputs = this.view.element.querySelectorAll("input[name=\"search[]\"]") as NodeListOf<HTMLInputElement>;
			for (i = 0; i < inputs.length; i++) {
				inputs[ i ].checked = data.search.indexOf(inputs[ i ].value as BookSearchField) !== -1;
			}
		}
		if (typeof data.language !== "undefined") {
			inputs = this.view.element.querySelectorAll("input[name=\"language[]\"]") as NodeListOf<HTMLInputElement>;
			for (i = 0; i < inputs.length; i++) {
				inputs[ i ].checked = data.language.indexOf(inputs[ i ].value) !== -1;
			}
		}
		if (typeof data.audio !== "undefined") {
			input = this.view.element.querySelector("input[name=\"audio\"][value=\"" + data.audio + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		if (typeof data.length !== "undefined") {
			input = this.view.element.querySelector("input[name=\"length\"][value=\"" + data.length + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		this.formState.doGroupChange();
	}

	public export (): BookSearchQuery {
		const data = this.formState.export();
		const dirtyFields = this.formState.listDirty();
		const out: BookSearchQuery = { };

		if (dirtyFields.search) {
			out.search = data.search;
		}
		if (dirtyFields.language) {
			out.language = data.language;
		}
		if (dirtyFields.length) {
			out.length = data.length;
		}
		if (dirtyFields.guidedReading) {
			out.guidedReading = [
				Misc.guidedReadingLevel(data.guidedReading[0]),
				Misc.guidedReadingLevel(data.guidedReading[1])
			];
		}
		if (dirtyFields.gradeLevel) {
			out.gradeLevel = data.gradeLevel.map(Number);
		}
		if (dirtyFields.lexile) {
			out.lexile = data.lexile.map(Number);
		}
		if (dirtyFields.atos) {
			out.atos = data.atos.map(Number);
		}
		if (dirtyFields.fiction) {
			if (Object.keys(data.fiction).length === 2) {
				delete out.fiction;
			} else {
				out.fiction = (Object.keys(data.fiction)[0] == "1");
			}
		}
		if (dirtyFields.audio && typeof data.audio !== "undefined") {
			out.audio = Number(data.audio) ? 1 : 0;
		}

		return out;
	}

	public resetField(fieldName: keyof BookSearchQuery) : void {
		const inputs = this.view.element.querySelectorAll('[name^="' + fieldName + '"]');

		for (let i = 0; i < inputs .length; i++) {
			const input = inputs[i] as HTMLInputElement;
			const tag = input.tagName.toUpperCase();
			const type = input.getAttribute("type");

			if (tag == "INPUT" && (type == "checkbox" || type == "radio")) {
				input.checked = input.defaultChecked;
			} else {
				input.value = input.defaultValue;
			}
		}

		if (fieldName == "gradeLevel") {
			this.gradeLevelSlider.refresh();
		} else if (fieldName == "lexile") {
			this.lexileSlider.refresh();
		} else if (fieldName == "guidedReading") {
			this.guidedReadingSlider.refresh();
		} else if (fieldName == "atos") {
			this.atosSlider.refresh();
		}

		this.formState.doGroupChange();
	}

	private init() {
		let lastDirty: string|null = null;
		this.formState.onChange(() => {
			const dirtyInputs = this.formState.listDirty();
			const out: Array<keyof BookSearchQuery> = [];
			for (const name in dirtyInputs) {
				if (dirtyInputs.hasOwnProperty(name)) {
					out.push(name as keyof BookSearchQuery);
				}
			}
			const dirty = JSON.stringify(out);
			if (dirty != lastDirty) {
				lastDirty = dirty;
				this.onFiltersUpdated.trigger({
					filters: out
				});
			}
			this.onChange.trigger({ });
		});
	}

	private initSlider(a11yLabel: string, numLabels: number, name: string, min: number, max: number, step: number, formatter: (value: number) => string, label: HTMLElement, parent: HTMLElement) : RangeSlider {
		const slider = new Slider<[number, number], [string, string]>(name, [min, max], min, max);
		if (formatter) {
			slider.setFormatter(formatter);
			slider.setAriaFormatter(formatter);
		}

		slider.setStep(step);
		slider.label = a11yLabel;
		slider.setTall();
		slider.setDarkMode(this.view.dataset.dark);
		slider.defaultValue = [ min, max ];

		label.innerHTML = slider.getValueFormatted().join(" - ");
		slider.onSelect(() => {
			this.formState.doGroupChange();
		});
		slider.onDrag(function(e) {
			label.innerHTML = e.label.join(" - ");
		});
		slider.showNLabels(numLabels);
		parent.appendChild(slider.element);
		return slider;
	}

	private setActiveFilter(filter: BookSearchTabId) : void {
		this.tabset.showTab(filter);
		this.view.filterDisplay.setAttribute("data-filter", filter);
	}

	public selectSearchFields(filters: BookSearchField[]) : void {
		this.view.searchFields.forEach(view => {
			view.checkbox.checked = (filters.indexOf(view.dataset.id) >= 0);
		});
	}
}
